/**
 * Scoring: the only model Digester calls is Jev (TypeSafe), non-generative, pinned to jev-1.13.0.
 *
 * Shape (measured in jev-vs-semantics-test): ONE request per message carrying ONE Noul question
 * per topic. The Noul probability x 100, rounded, is the 0..100 relevance score. Folder routing is
 * NOT a model question — it is done by scoring rules over these scores (engine.md).
 *
 * Two implementations of ScoringProvider:
 *   - device: HTTP client of our proxy, POST /api/score (the key never reaches a client)
 *   - server: direct Jev client, used by the proxy and by the server engine
 */

import type { MessageId, Score, TopicId } from './ids.js';

/** The pinned model id. Never an alias such as 'jev-latest': tuned thresholds are version-specific. */
export const JEV_MODEL = 'jev-1.13.0' as const;
export type ScoringModel = typeof JEV_MODEL;

export interface ScoreTopic {
  id: TopicId;
  /** 1..80 chars. Jev reads the topic NAME only (models.md §5.0: which Topic fields a model reads is a setting of the model). */
  name: string;
  /**
   * Not sent from data schema 3 on. A device of an older bundle still sends its topic description here; the
   * proxy accepts it (≤ 500 chars) and IGNORES it — it is neither asked nor part of the cache key.
   */
  hint?: string;
}

export interface ScoreItem {
  id: MessageId;
  /** Source display name. */
  source: string;
  title: string;
  /** Plain text, already cut by the caller to the Jev record's options.bodyMaxChars (≤ scoring.body_max_chars). */
  body: string;
}

export interface ScoreRequest {
  model: ScoringModel;
  /** Enabled topics, 1..(plan topics limit). */
  topics: ScoreTopic[];
  /** 1..jev.max_items_per_request items. */
  items: ScoreItem[];
  /**
   * The person's daily budget when it is set: the Jev model record's `options.dailyBudget` (data schema 2
   * kept it in `settings.scoring.dailyLimit`; the migration to 3 moved it). The proxy counts against
   * min(plan's limit, maxPerDay), so several devices cannot overspend it together, and answers plan_limit
   * (`scored_per_day`) with `details.budget = true` when that is reached. The one enforcement point.
   */
  maxPerDay?: number;
}

export type ScoreItemResult =
  | {
      id: MessageId;
      ok: true;
      /** One entry per requested topic id. */
      scores: Record<string, Score>;
      /** Input tokens billed by Jev for this item (0 when served from the cache). */
      inputTokens: number;
      cached: boolean;
    }
  | {
      id: MessageId;
      ok: false;
      /** 'unavailable' = retry later; 'rejected' = this item will never score (too large, empty). */
      error: 'unavailable' | 'rejected';
      message: string;
    };

export interface ScoreResponse {
  model: ScoringModel;
  results: ScoreItemResult[];
  /** Messages scored today for this account after this call, and the daily maximum. */
  usedToday: number;
  maxPerDay: number;
}

/**
 * Invariants
 *  - Rejects with DigesterError not_entitled (plan has no AI scoring), plan_limit
 *    (limit = 'messages_per_day'), rate_limited, unauthenticated, or scoring_unavailable
 *    (whole request failed). Per-item failures come back inside `results`.
 *  - Idempotent per (message id, topic set): repeating a request is answered from the cache and is
 *    not billed or counted again.
 */
export interface ScoringProvider {
  score(request: ScoreRequest): Promise<ScoreResponse>;
}

// ── Jev wire format (server side only) ─────────────────────────────────────────────────────────

/** POST https://api.typesafe.ai/v1/systemone, Authorization: Bearer <TYPESAFE_API_KEY>. */
export interface JevRequest {
  /** Named fields, not one blob: Jev's accuracy drops with unrelated state. */
  state: { source: string; title: string; body: string };
  model: ScoringModel;
  /** Key = `t_${topicId}`. */
  questions: Record<string, JevNoulQuestion>;
}

export interface JevNoulQuestion {
  type: 'noul';
  /** `Is this message substantively about the following topic? ${name}` (the name only, models.md §5.0). */
  instructions: string;
  criteria: {
    /** `The message is genuinely about ${name}.` */
    true: string;
    /** `The message is not about ${name}, or only mentions it in passing.` */
    false: string;
  };
}

export interface JevResponse {
  model: string;
  answers: Record<string, { type: 'noul'; noul: number }>;
  usage: { input_tokens: number; output_tokens: number };
}
